import { InsuranceType } from "../../../../../type/Appointment.js";
import * as Yup from "yup";
import { MaskedDateInput } from "../../components/masked-date-input/masked-date-input.js";
import { isPatientUnderaged } from "../entities/Form.js";
import { createFields } from "../helpers/createFields.js";
import { resetFields } from "../helpers/resetFields.js";
import { toBoolean } from "../helpers/toBoolean.js";
import { buildValidationSchema } from "../yup/buildValidationSchema.js";
import { adultValid, dateValidation } from "../yup/yup.js";
import "react/jsx-runtime";
import "react-i18next";
import "react-text-mask";
import "../../../ana-toolbox/ana-toolbox.js";
import "../../../../../constants/date/date.js";
import "date-fns/fp/format";
import "date-fns/isValid";
import "date-fns/parse";
import "date-fns";
import "../../../../../utils/pipe.js";
import "date-fns/fp";
import "libphonenumber-js/max";
import "libphonenumber-js/mobile";
import "../helpers/predicates.js";
import "../yup/emailValidation.js";
Yup.addMethod(Yup.string, "adult", adultValid);
Yup.addMethod(Yup.string, "birthday", dateValidation);
const insuranceOptions = [
  { label: "option.private_insurance", value: InsuranceType.PRIVATE_INSURANCE },
  { label: "option.social_insurance", value: InsuranceType.SOCIAL_INSURANCE }
];
const yesNoOptions = [
  { label: "yes", value: "true" },
  { label: "no", value: "false" }
];
const assistanceBasisOptions = [
  { label: "option.handicap", value: "HANDICAP" },
  { label: "option.limited_everyday_competence", value: "LIMITED_EVERYDAY_COMPETENCE" }
];
const hasFamilyInsurance = (form) => toBoolean(form.hasFamilyInsurance);
const isPrivatelyInsured = (form) => form.type === InsuranceType.PRIVATE_INSURANCE;
const livesOnDifferentAddress = (form) => hasFamilyInsurance(form) && toBoolean(form.livesOnDifferentAddress);
const hasDentalInsurance = (form) => toBoolean(form.hasDentalInsurance);
const insuranceFields = (email) => createFields({
  hasCareLevel: {
    __type: "radioGroup",
    label: "field.hasCareLevel",
    name: "hasCareLevel",
    options: yesNoOptions,
    isRequired: true,
    validation: Yup.string().required(),
    hooks: {
      beforeChange: (value, helpers, _event) => {
        if (value === "false") {
          resetFields(["careLevelLimitedInTime"], helpers);
        }
      }
    }
  },
  careLevelLimitedInTime: {
    __type: "radioGroup",
    label: "field.careLevelLimitedInTime",
    name: "careLevelLimitedInTime",
    options: yesNoOptions,
    isRequired: true,
    helperText: email ? "field.careLevelLimitedInTimeEmail_helper" : "field.careLevelLimitedInTime_helper",
    helperData: { email },
    visibleWhen: ({ hasCareLevel }) => hasCareLevel === "true" || hasCareLevel === true,
    validation: Yup.mixed().when("hasCareLevel", {
      is: (value) => value === "true" || value === true,
      then: Yup.mixed().required(),
      otherwise: Yup.mixed()
    })
  },
  entitledToIntegrationAssistance: {
    __type: "radioGroup",
    label: "field.entitledToIntegrationAssistance",
    name: "entitledToIntegrationAssistance",
    options: yesNoOptions,
    isRequired: true,
    validation: Yup.string().required(),
    hooks: {
      beforeChange: (value, helpers, _event) => {
        if (value === "false") {
          resetFields(["integrationAssistanceBasedOnType"], helpers);
        }
      }
    }
  },
  integrationAssistanceBasedOnType: {
    __type: "radioGroup",
    label: "field.integrationAssistanceBasedOnType",
    name: "integrationAssistanceBasedOnType",
    isRequired: true,
    options: assistanceBasisOptions,
    visibleWhen: ({ entitledToIntegrationAssistance }) => entitledToIntegrationAssistance === "true" || entitledToIntegrationAssistance === true,
    helperText: email ? "field.integrationAssistanceBasedOnTypeEmail_helper" : "field.integrationAssistanceBasedOnType_helper",
    helperData: { email },
    validation: Yup.mixed().when("entitledToIntegrationAssistance", {
      is: (value) => value === "true" || value === true,
      then: Yup.mixed().required(),
      otherwise: Yup.mixed()
    })
  },
  type: {
    __type: "radioGroup",
    label: "field.insurance_type",
    name: "type",
    options: insuranceOptions,
    isRequired: true,
    validation: Yup.string().required("insurance_type_error"),
    helperText: "field.insurance_type_helper",
    hooks: {
      beforeChange: (value, helpers, _event) => {
        if (typeof value !== "string")
          return;
        const birthday = helpers.getFieldProps("birthday").value;
        resetFields(
          ["insuranceCompany", "insuranceCompanyId", "dentalInsuranceCompany", "dentalInsuranceCompanyId"],
          helpers
        );
        if (value === InsuranceType.PRIVATE_INSURANCE) {
          helpers.setFieldValue("hasDentalInsurance", false);
        }
        if (value === InsuranceType.SOCIAL_INSURANCE) {
          helpers.setFieldValue("hasCivilServiceAllowance", false);
        }
        if (isPatientUnderaged({ birthday })) {
          if (value === InsuranceType.SOCIAL_INSURANCE) {
            helpers.setFieldValue("hasFamilyInsurance", true);
          } else {
            helpers.setFieldValue("hasFamilyInsurance", false);
            resetFields(
              ["holderFirstName", "holderLastName", "holderBirthday", "holderCity", "holderStreet", "holderZipcode"],
              helpers
            );
          }
        }
      }
    }
  },
  insuranceCompany: {
    __type: "custom",
    label: "field.insurance_company_label",
    name: "insuranceCompany",
    inputProps: {
      type: "text"
    },
    isRequired: true,
    validation: Yup.string().when(["type"], {
      is: (type) => type === InsuranceType.PRIVATE_INSURANCE,
      then: Yup.string().required(),
      otherwise: Yup.string()
    }),
    helperText: "field.insurance_company_helper",
    visibleWhen: isPrivatelyInsured
  },
  hasFamilyInsurance: {
    __type: "toggle",
    label: "field.has_family_insurance_label",
    name: "hasFamilyInsurance",
    inputProps: {
      withText: true
    },
    validation: Yup.boolean(),
    helperText: "field.family_insurance_helper",
    hooks: {
      beforeChange: (value, helpers, _event) => {
        if (typeof value !== "boolean")
          return;
        if (value)
          return;
        resetFields(
          [
            "holderFirstName",
            "holderLastName",
            "holderBirthday",
            "holderStreet",
            "holderCity",
            "holderZipcode",
            "livesOnDifferentAddress"
          ],
          helpers
        );
      }
    },
    visibleWhen: (form) => {
      const isUnderaged = isPatientUnderaged(form);
      const privatelyInsured = form.type === InsuranceType.PRIVATE_INSURANCE;
      return !(isUnderaged && privatelyInsured);
    }
  },
  holderFirstName: {
    __type: "text",
    label: "field.insurance_holder_first_name_v2",
    name: "holderFirstName",
    inputProps: {
      type: "text"
    },
    isRequired: true,
    validation: Yup.string().when("hasFamilyInsurance", {
      is: true,
      then: Yup.string().required("holder_first_name_error"),
      otherwise: Yup.string()
    }),
    visibleWhen: hasFamilyInsurance
  },
  holderLastName: {
    __type: "text",
    label: "field.insurance_holder_last_name_v2",
    name: "holderLastName",
    inputProps: {
      type: "text"
    },
    isRequired: true,
    validation: Yup.string().when("hasFamilyInsurance", {
      is: true,
      then: Yup.string().required("holder_last_name_error"),
      otherwise: Yup.string()
    }),
    visibleWhen: hasFamilyInsurance
  },
  holderBirthday: {
    __type: "text",
    label: "field.insurance_holder_date_of_birth_v2",
    name: "holderBirthday",
    inputProps: {
      type: "text",
      inputComponent: MaskedDateInput,
      InputLabelProps: {
        shrink: true
      }
    },
    isRequired: true,
    validation: Yup.string().when("hasFamilyInsurance", {
      is: true,
      then: Yup.string().birthday({ empty: "birthday_form_error", invalidDate: "invalid_date", inPast: "in_past" }).adult("adult_age").required(),
      otherwise: Yup.string()
    }),
    visibleWhen: hasFamilyInsurance
  },
  livesOnDifferentAddress: {
    __type: "toggle",
    label: "field.lives_on_different_address",
    name: "livesOnDifferentAddress",
    inputProps: {
      withText: true
    },
    validation: Yup.boolean(),
    visibleWhen: hasFamilyInsurance,
    helperText: "field.lives_on_different_address_helper",
    hooks: {
      beforeChange: (_value, helpers, _event) => {
        resetFields(["holderStreet", "holderCity", "holderZipcode"], helpers);
      }
    }
  },
  holderCity: {
    __type: "text",
    label: "field.insurance_family_city_v2",
    name: "holderCity",
    inputProps: {
      type: "text"
    },
    isRequired: true,
    validation: Yup.string().when(["livesOnDifferentAddress", "hasFamilyInsurance"], {
      is: (livesOnDifferentAddress2, hasFamilyInsurance2) => livesOnDifferentAddress2 && hasFamilyInsurance2,
      then: Yup.string().required("holder_city_error"),
      otherwise: Yup.string()
    }),
    visibleWhen: livesOnDifferentAddress
  },
  holderStreet: {
    __type: "text",
    label: "field.insurance_family_street_and_number_v2",
    name: "holderStreet",
    inputProps: {
      type: "text"
    },
    isRequired: true,
    validation: Yup.string().when(["livesOnDifferentAddress", "hasFamilyInsurance"], {
      is: (livesOnDifferentAddress2, hasFamilyInsurance2) => livesOnDifferentAddress2 && hasFamilyInsurance2,
      then: Yup.string().required("holder_street_and_number_error"),
      otherwise: Yup.string()
    }),
    visibleWhen: livesOnDifferentAddress
  },
  holderZipcode: {
    __type: "text",
    label: "field.insurance_family_postal_code_v2",
    name: "holderZipcode",
    isRequired: true,
    validation: Yup.string().when(["livesOnDifferentAddress", "hasFamilyInsurance"], {
      is: (livesOnDifferentAddress2, hasFamilyInsurance2) => livesOnDifferentAddress2 && hasFamilyInsurance2,
      then: Yup.string().required("holder_zip_code_error"),
      otherwise: Yup.string()
    }),
    visibleWhen: livesOnDifferentAddress
  },
  hasDentalInsurance: {
    __type: "toggle",
    label: "field.supplementary_dental_insurance",
    name: "hasDentalInsurance",
    inputProps: {
      withText: true
    },
    validation: Yup.boolean(),
    helperText: "field.supplementary_dental_insurance_helper",
    visibleWhen: ({ type }) => type === InsuranceType.SOCIAL_INSURANCE,
    hooks: {
      beforeChange: (value, helpers, _event) => {
        if (value !== false)
          return;
        resetFields(["dentalInsuranceCompany", "dentalInsuranceCompanyId"], helpers);
      }
    }
  },
  dentalInsuranceCompany: {
    __type: "custom",
    label: "field.dental_insurance_company_label",
    name: "dentalInsuranceCompany",
    inputProps: {
      type: "text"
    },
    isRequired: true,
    helperText: "field.dental_insurance_company_helper",
    validation: Yup.string().when("hasDentalInsurance", {
      is: true,
      then: Yup.string().required("dental_insurance_company_name_error"),
      otherwise: Yup.string()
    }),
    visibleWhen: hasDentalInsurance
  },
  hasCivilServiceAllowance: {
    __type: "toggle",
    label: "field.has_civil_service_allowance_label",
    helperText: "field.has_civil_service_allowance_helper",
    name: "hasCivilServiceAllowance",
    inputProps: {
      withText: true
    },
    visibleWhen: ({ type }) => type === InsuranceType.PRIVATE_INSURANCE,
    validation: Yup.boolean()
  }
});
const insuranceFieldsValidationSchema = (email) => buildValidationSchema(insuranceFields(email));
export {
  insuranceFields,
  insuranceFieldsValidationSchema
};
